// MarkerFactory — the map-object marker builder Facade (map-architecture Phase 5).
//
// A plain TypeScript class (NOT React). Encapsulates the four marker-builder
// functions that previously lived at MapCanvas component scope (P4):
//   * createObjectMarker      — shared low-level sprite→container builder;
//   * createRealEstateMarker  — house strategy (real-estate sprite);
//   * createMapObjectMarker   — static-object Strategy switch
//                               (cityhall / shop / hell_of_flame / decor_* / other);
//   * createQuestMarker       — quest-overlay strategy.
//
// Factory + Strategy: `build(mapObject)` dispatches by object type to the
// per-type strategy methods (house → createRealEstateMarker, everything else →
// createMapObjectMarker, whose internal branch selects the concrete
// cityhall/shop/hof/decor image + size strategy). Plot markers are built by the
// separate synchronous `createPlotIcon` (map/render/mapMarkers.ts) and quest
// overlays via `createQuestMarker`; both keep their dedicated entry points
// because their signatures + registration flows differ from build()'s.
//
// The bodies are moved VERBATIM from MapCanvas; the only change is closure
// variable → context access. The ~43-dep P4 estimate collapsed to FOUR context
// members once the render/registration/highlight/click clusters had already been
// extracted in the earlier phases: a shared texture-path Set, the decor-atlas
// getter (reached through the MapController facade), and the two const size
// lookup tables (kept in MapCanvas because they have other consumers there, so
// passed in by value — mirroring how SceneManager receives MERCURY_X etc.).
import * as PIXI from 'pixi.js';
import { MapObject } from '../../../../models/mapObject';
import { getDecorImagePath, isDecorInteractiveType } from '../../../../data/mapDecorAssets';
import { createDebugPoints } from '../../render/mapMarkers';
import type { MapMarkerContainer } from '../../render/mapPixiExtensions';
import { houseImagePath } from '../../mapPreload';

export interface MarkerFactoryContext {
  // Shared mutable Set of loaded texture paths (same reference MapCanvas + the
  // scene/render subsystems track — value passed in, not copied).
  mapLoadedTexturePaths: Set<string>;
  // Getter for the current decor spritesheet atlas (owned by SceneManager,
  // reached through the MapController facade).
  getDecorAtlasSheet: () => PIXI.Spritesheet | null;
  // Const size lookup tables — kept in MapCanvas (other consumers there), passed
  // by value.
  REAL_ESTATE_FIXED_SIZES: Record<number, { width: number; height: number }>;
  STATIC_OBJECT_FIXED_SIZES: Record<string, { width: number; height: number }>;
}

export class MarkerFactory {
  private ctx: MarkerFactoryContext;

  constructor(ctx: MarkerFactoryContext) {
    this.ctx = ctx;
  }

  // ── Factory dispatch ───────────────────────────────────────────────────────
  /**
   * Factory entry: dispatch a static / real-estate map object to its per-type
   * strategy. House → real-estate strategy; every other static type →
   * createMapObjectMarker (which itself branches to the concrete
   * cityhall/shop/hof/decor strategy). Plot + quest markers have dedicated
   * builders (createPlotIcon / createQuestMarker) and are not routed here.
   */
  build = (mapObject: MapObject): Promise<PIXI.Container | null> => {
    if (mapObject.type === 'house') return this.createRealEstateMarker(mapObject);
    return this.createMapObjectMarker(mapObject);
  };

  // ── Quest-overlay strategy ─────────────────────────────────────────────────
  createQuestMarker = async (
    mapObject: MapObject,
    markerContainer: PIXI.Container,
    markerImagePath: string
  ): Promise<PIXI.Container | null> => {
    const { mapLoadedTexturePaths, STATIC_OBJECT_FIXED_SIZES } = this.ctx;
    try {
      const texture = await PIXI.Assets.load(markerImagePath);
      mapLoadedTexturePaths.add(markerImagePath);
      // Увімкнення міпмапів для усунення мерехтіння при русі
      if (texture.source) {
        texture.source.autoGenerateMipmaps = true;
        texture.source.scaleMode = 'linear';
      }
      const questMarker = new PIXI.Sprite(texture);

      questMarker.width = 52.38;
      questMarker.height = 66;
      questMarker.anchor.set(0.5, 1.0);

      const sprite = markerContainer.children[0] as PIXI.Sprite;
      if (!sprite || !sprite.texture) return null;

      let spriteHeight = 0;
      let anchorY = 0.5;

      const fixedSize = STATIC_OBJECT_FIXED_SIZES[mapObject.type];
      if (fixedSize) {
        spriteHeight = fixedSize.height;
        anchorY = 0.5;
      } else {
        return null;
      }

      const topY = -anchorY * spriteHeight;
      questMarker.position.set(0, topY);

      const questMarkerContainer = new PIXI.Container();
      questMarkerContainer.addChild(questMarker);

      questMarkerContainer.position.set(markerContainer.x, markerContainer.y);
      questMarkerContainer.zIndex = 10000 + mapObject.y;

      questMarkerContainer.eventMode = 'static';
      questMarkerContainer.interactiveChildren = true;
      questMarkerContainer.cursor = 'pointer';
      questMarkerContainer.cullable = true;

      const questMk = questMarkerContainer as MapMarkerContainer;
      questMk.mapObjectData = mapObject;
      questMk.mainMarker = markerContainer;

      return questMarkerContainer;
    } catch (error) {
      console.error(`[QuestMarker] Failed to load quest marker for ${mapObject.type} id ${mapObject.id}:`, error);
      return null;
    }
  };

  // ── Shared low-level builder (template for the strategies below) ────────────
  createObjectMarker = async (
    mapObject: MapObject,
    imagePath: string,
  _scale: number,
    hasFallback: boolean = false,
    fixedSize?: { width: number; height: number },
    // When provided (static decor from the shared atlas), use this texture
    // directly instead of fetching imagePath — the atlas baseTexture is already
    // loaded/tracked, so we skip the per-file Assets.load + path tracking.
    preloadedTexture?: PIXI.Texture
  ): Promise<PIXI.Container | null> => {
    const { mapLoadedTexturePaths } = this.ctx;
    const container = new PIXI.Container();

    try {
      let texture: PIXI.Texture;
      if (preloadedTexture) {
        texture = preloadedTexture;
      } else {
        texture = await PIXI.Assets.load(imagePath);
        mapLoadedTexturePaths.add(imagePath);
      }
      // Mipmaps + linear filtering for all map objects to prevent flickering
      if (texture.source) {
        texture.source.autoGenerateMipmaps = true;
        texture.source.scaleMode = 'linear';
      }
      const sprite = new PIXI.Sprite(texture);
      sprite.anchor.set(0.5, 0.5);

      // Якщо є display_width/display_height з БД — використовуємо їх
      if (mapObject.display_width && mapObject.display_height) {
        sprite.width = mapObject.display_width;
        sprite.height = mapObject.display_height;
      } else if (mapObject.type.startsWith('decor_')) {
        // Для decor об'єктів без розмірів у БД — природний розмір текстури
      } else {
        // Для інших об'єктів використовуємо fixedSize
        if (!fixedSize) {
          console.error(`[MapCanvas] Fixed size is required for ${mapObject.type} id ${mapObject.id}`);
          return null;
        }
        sprite.width = fixedSize.width;
        sprite.height = fixedSize.height;
      }

      // Застосовуємо відзеркалення для будинків на основі mirror_direction.
      // Важливо: не задаємо scale.x = -1 напряму, щоб не втратити базовий масштаб.
      if (mapObject.type === 'house') {
        const baseScaleX = Math.abs(sprite.scale.x || 1);
        sprite.scale.x = mapObject.mirror_direction === 'right' ? -baseScaleX : baseScaleX;
      }
      if (mapObject.data?.rotate_180) {
        sprite.scale.x = -Math.abs(sprite.scale.x);
      }

      container.addChild(sprite);
      const spriteWidth = Math.abs(sprite.width);
      const spriteHeight = Math.abs(sprite.height);
      createDebugPoints(container, spriteWidth, spriteHeight, 0.5, 0.5);
    } catch (error) {
      console.error(`[MapCanvas] Failed to load image for ${mapObject.type} id ${mapObject.id} from ${imagePath}:`, error);

      if (hasFallback) {
        const marker = new PIXI.Graphics();
        marker.circle(0, 0, 20).fill(0x8B4513);
        marker.circle(0, 0, 20).stroke({ width: 2, color: 0x000000 });
        container.addChild(marker);
      } else {
        return container;
      }
    }

    container.position.set(mapObject.x, mapObject.y);
    container.zIndex = mapObject.y;
    container.eventMode = 'static';
    container.cursor = 'pointer';
    (container as MapMarkerContainer).mapObjectData = mapObject;
    container.cullable = true;

    return container;
  };

  // ── House (real-estate) strategy ───────────────────────────────────────────
  createRealEstateMarker = async (mapObject: MapObject): Promise<PIXI.Container> => {
    const { REAL_ESTATE_FIXED_SIZES } = this.ctx;
    const realEstateId = mapObject.data?.real_estate_id;
    if (!realEstateId) {
      const container = new PIXI.Container();
      container.position.set(mapObject.x, mapObject.y);
      container.zIndex = mapObject.y;
      container.eventMode = 'static';
      container.cursor = 'pointer';
      (container as MapMarkerContainer).mapObjectData = mapObject;
      container.cullable = true;
      return container;
    }

    const imagePath = houseImagePath(realEstateId);
    const fixedSize = REAL_ESTATE_FIXED_SIZES[realEstateId];
    if (!fixedSize) {
      console.error(`[MapCanvas] No fixed size defined for real estate id ${realEstateId}`);
      return new PIXI.Container();
    }
    const marker = await this.createObjectMarker(mapObject, imagePath, 0, false, fixedSize);
    return marker || new PIXI.Container();
  };

  // ── Static-object strategy switch (cityhall / shop / hof / decor / other) ───
  createMapObjectMarker = async (mapObject: MapObject): Promise<PIXI.Container | null> => {
    const { mapLoadedTexturePaths, STATIC_OBJECT_FIXED_SIZES, getDecorAtlasSheet } = this.ctx;

    // Спеціальні випадки з явними маппінгами
    let imagePath: string;
    if (mapObject.type === 'cityhall') {
      imagePath = `/game-assets/map-objects/cityhall.webp`;
    } else if (mapObject.type === 'shop') {
      imagePath = `/game-assets/map-objects/shop.webp`;
    } else if (mapObject.type === 'hell_of_flame') {
      imagePath = `/game-assets/map-objects/hall-of-fame.webp`;
    } else if (mapObject.type.startsWith('decor_')) {
      imagePath = getDecorImagePath(mapObject.type);
    } else {
      // Для всіх інших типів: спробувати завантажити з кореня map-objects
      imagePath = `/game-assets/map-objects/${mapObject.type}.webp`;
    }

    // Для decor об'єктів використовуємо фактичні розміри файлу, не задаємо fixedSize
    let fixedSize: { width: number; height: number } | undefined;

    if (mapObject.type.startsWith('decor_')) {
      // Для decor об'єктів не передаємо fixedSize, щоб використовувались фактичні розміри
      fixedSize = undefined;
    } else {
      // Для інших об'єктів отримуємо розміри зі статичних або завантажуємо
      fixedSize = STATIC_OBJECT_FIXED_SIZES[mapObject.type];

      if (!fixedSize) {
        try {
          // Спробуємо завантажити зображення, щоб отримати його розміри
          const texture = await PIXI.Assets.load(imagePath);
          mapLoadedTexturePaths.add(imagePath);
          fixedSize = { width: texture.width, height: texture.height };
        } catch (error) {
          console.error(`[MapCanvas] Failed to load image for map object type ${mapObject.type} from ${imagePath}:`, error);
          // Якщо не вдалося завантажити, використовуємо розміри за замовчуванням
          fixedSize = { width: 100, height: 100 };
        }
      }
    }

    const hasFallback = mapObject.type === 'cityhall';

    // Static decor: pull the frame from the shared spritesheet atlas instead of
    // an individual per-file fetch. Interactive decor and any not-yet-packed
    // frames fall through to the normal per-file load below.
    const decorAtlasSheet = getDecorAtlasSheet() ?? null;
    if (
      mapObject.type.startsWith('decor_') &&
      !isDecorInteractiveType(mapObject.type) &&
      decorAtlasSheet
    ) {
      const frame = decorAtlasSheet.textures[`${mapObject.type}.webp`];
      if (frame) {
        return await this.createObjectMarker(mapObject, imagePath, 0, hasFallback, fixedSize, frame);
      }
    }

    return await this.createObjectMarker(mapObject, imagePath, 0, hasFallback, fixedSize);
  };
}
